import { newNotificationsCountPath } from "@circle-react/helpers/urlHelpers";
import type { CommunitySwitcher } from "@circle-react/types/CommunitySwitcher";
import type { Dispatch, SetStateAction } from "react";

import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";

export const COMMUNITY_SWITCH_EVENT = "community-switch";

export const notifyCommunitySwitch = (): void => {
  window.dispatchEvent(new Event(COMMUNITY_SWITCH_EVENT));
};

export const getAutomaticLoginRootUrl = (rootUrl: string, token: string) => {
  const url = new URL(rootUrl);
  url.searchParams.set("automatic_login", "true");
  url.searchParams.set("token", token);

  return url.toString();
};

/* cspell:disable-next-line */
const PENDO_NEW_COMMUNITY_GUIDE_ID = "0oguJmJrou1SpPPXRwwyzbd4Wlo";

export const DISCOVER_ROUTE = window.DISCOVER_BASE_URL || "/discover";

/**
 * Appends the community-switcher attribution params Discover uses for
 * analytics onto a freshly generated Discover login URL.
 *
 * Shared by the desktop community switcher (`useCommunitySwitcherAdapter`)
 * and the mobile drawer equivalent (`MobileCommunitySwitcherDrawerContent`)
 * so both entry points redirect to Discover identically.
 */
export const getDiscoverLoginUrl = (
  loginUrl: string,
  communityId?: number,
): string => {
  const url = new URL(loginUrl, globalThis.location.origin);
  url.searchParams.set("source", "community_switcher");
  if (communityId) {
    url.searchParams.set("community_id", String(communityId));
  }
  return url.toString();
};

export const shouldUsePendoOnNewCommunityLink = (
  isAdmin: boolean | undefined,
  pathname: string,
  planStatus: string | undefined,
): boolean =>
  Boolean(isAdmin) &&
  pathname.includes("settings/plans") &&
  planStatus === "Expired Trial";

export const buildNewCommunityHrefWithPendo = (
  pathname: string,
  search: string,
  hash: string,
): string => {
  const url = new URL(
    `${globalThis.location.origin}${pathname}${search}${hash}`,
  );
  url.searchParams.set("pendo", PENDO_NEW_COMMUNITY_GUIDE_ID);
  return url.toString();
};

const mergeNotificationCount = (
  previous: Record<number, number>,
  communityId: number,
  count: number,
): Record<number, number> =>
  previous[communityId] === count
    ? previous
    : { ...previous, [communityId]: count };

export type NotificationCountSetter = Dispatch<
  SetStateAction<Record<number, number>>
>;

const applyNotificationCountResponse = (
  setCounts: NotificationCountSetter,
  communityId: number,
  count: number,
) => {
  setCounts(previous => mergeNotificationCount(previous, communityId, count));
};

export const requestNotificationCountForCommunity = (
  community: CommunitySwitcher,
  setCounts: NotificationCountSetter,
) => {
  void reactQueryGet(
    `${newNotificationsCountPath()}?community_id=${community.id}`,
  ).then((data: { new_notifications_count: number }) => {
    applyNotificationCountResponse(
      setCounts,
      community.id,
      data.new_notifications_count,
    );
  });
};
